import React from "react";
import { Box, Container, Typography, Divider, Stack } from "@mui/material";

const headingFont = `"Bodoni Moda", serif`;

const PrivacyPolicyPage: React.FC = () => {
  return (
    <Box
      sx={{
        backgroundColor: "#000000",
        color: "rgba(255,255,255,0.86)",
        minHeight: "100vh",
        py: { xs: 8, md: 10 },
      }}
    >
      <Container maxWidth="md">
        {/* Page Title */}
        <Typography
          variant="h3"
          sx={{
            fontFamily: headingFont,
            fontWeight: 700,
            letterSpacing: ".06em",
            textTransform: "uppercase",
            textAlign: "center",
          }}
        >
          Privacy Policy
        </Typography>

        <Typography
          variant="subtitle2"
          sx={{
            textAlign: "center",
            mt: 1.5,
            color: "rgba(255,255,255,0.6)",
          }}
        >
          Last updated: October 31, 2024
        </Typography>

        <Divider
          sx={{
            my: 5,
            borderColor: "rgba(255,255,255,0.12)",
          }}
        />

        <Stack spacing={4}>
          {/* Intro */}
          <Box>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              This Privacy Policy describes how Haroon Sharif Jewellers (the
              &quot;Site&quot;, &quot;we&quot;, &quot;us&quot;, or
              &quot;our&quot;) collects, uses, and discloses your personal
              information when you visit, use our services, or make a purchase
              from haroonsharifjewellers.com (the &quot;Site&quot;) or otherwise
              communicate with us regarding the Site (collectively, the
              &quot;Services&quot;). For purposes of this Privacy Policy,
              &quot;you&quot; and &quot;your&quot; means you as the user of the
              Services, whether you are a customer, website visitor, or another
              individual whose information we have collected pursuant to this
              Privacy Policy.
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              Please read this Privacy Policy carefully. By using and accessing
              any of the Services, you agree to the collection, use, and
              disclosure of your information as described in this Privacy
              Policy. If you do not agree to this Privacy Policy, please do not
              use or access any of the Services.
            </Typography>
          </Box>

          {/* Changes */}
          <Box component="section">
            <Typography
              variant="h5"
              sx={{
                fontFamily: headingFont,
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              Changes to This Privacy Policy
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              We may update this Privacy Policy from time to time, including to
              reflect changes to our practices or for other operational, legal,
              or regulatory reasons. We will post the revised Privacy Policy on
              the Site, update the &quot;Last updated&quot; date and take any
              other steps required by applicable law.
            </Typography>
          </Box>

          {/* How We Collect and Use */}
          <Box component="section">
            <Typography
              variant="h5"
              sx={{
                fontFamily: headingFont,
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              How We Collect and Use Your Personal Information
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              To provide the Services, we collect personal information about you
              from a variety of sources, as set out below. The information that
              we collect and use varies depending on how you interact with us.
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              In addition to the specific uses set out below, we may use
              information we collect about you to communicate with you, provide
              or improve the Services, comply with any applicable legal
              obligations, enforce any applicable terms of service, and to
              protect or defend the Services, our rights, and the rights of our
              users or others.
            </Typography>
          </Box>

          {/* What Personal Information We Collect */}
          <Box component="section">
            <Typography
              variant="h5"
              sx={{
                fontFamily: headingFont,
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              What Personal Information We Collect
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              The types of personal information we obtain about you depends on
              how you interact with our Site and use our Services. When we use
              the term &quot;personal information&quot;, we are referring to
              information that identifies, relates to, describes or can be
              associated with you. The following sections describe the
              categories and specific types of personal information we collect.
            </Typography>

            {/* Directly from you */}
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mt: 2, mb: 1 }}
            >
              Information We Collect Directly from You
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              Information that you directly submit to us through our Services
              may include:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  Contact details including your name, address, phone number,
                  and email.
                </Typography>
              </li>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  Order information including your name, billing address,
                  shipping address, payment confirmation, email address, and
                  phone number.
                </Typography>
              </li>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  Account information including your username, password,
                  security questions and other information used for account
                  security purposes.
                </Typography>
              </li>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  Customer support information including the information you
                  choose to include in communications with us, for example, when
                  sending a message through the Services.
                </Typography>
              </li>
            </Box>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              Some features of the Services may require you to directly provide
              us with certain information about yourself. You may elect not to
              provide this information, but doing so may prevent you from using
              or accessing these features.
            </Typography>

            {/* Usage data */}
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mt: 2, mb: 1 }}
            >
              Information We Collect about Your Usage
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              We may also automatically collect certain information about your
              interaction with the Services (&quot;Usage Data&quot;). To do
              this, we may use cookies, pixels and similar technologies
              (&quot;Cookies&quot;). Usage Data may include information about
              how you access and use our Site and your account, including device
              information, browser information, information about your network
              connection, your IP address and other information regarding your
              interaction with the Services.
            </Typography>

            {/* From third parties */}
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mt: 2, mb: 1 }}
            >
              Information We Obtain from Third Parties
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              Finally, we may obtain information about you from third parties,
              including from vendors and service providers who may collect
              information on our behalf, such as:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  Companies who support our Site and Services, such as Shopify.
                </Typography>
              </li>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  Our payment processors, who collect payment information (e.g.,
                  bank account, credit or debit card information, billing
                  address) to process your payment in order to fulfill your
                  orders and provide you with products or services you have
                  requested, in order to perform our contract with you.
                </Typography>
              </li>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  When you visit our Site, open or click on emails we send you,
                  or interact with our Services or advertisements, we, or third
                  parties we work with, may automatically collect certain
                  information using online tracking technologies such as pixels,
                  web beacons, software developer kits, third-party libraries,
                  and cookies.
                </Typography>
              </li>
            </Box>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              Any information we obtain from third parties will be treated in
              accordance with this Privacy Policy. Also see the section below,
              Third Party Websites and Links.
            </Typography>
          </Box>

          {/* How We Use Your Personal Information */}
          <Box component="section">
            <Typography
              variant="h5"
              sx={{
                fontFamily: headingFont,
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              How We Use Your Personal Information
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mt: 2, mb: 1 }}
            >
              Providing Products and Services
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              We use your personal information to provide you with the Services
              in order to perform our contract with you, including to process
              your payments, fulfill your orders, to send notifications to you
              related to your account, purchases, returns, exchanges or other
              transactions, to create, maintain and otherwise manage your
              account, to arrange for shipping, facilitate any returns and
              exchanges and other features and functionalities related to your
              account.
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mt: 2, mb: 1 }}
            >
              Marketing and Advertising
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              We may use your personal information for marketing and promotional
              purposes, such as to send marketing, advertising and promotional
              communications by email, text message or postal mail, and to show
              you advertisements for products or services. This may include
              using your personal information to better tailor the Services and
              advertising on our Site and other websites.
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mt: 2, mb: 1 }}
            >
              Security and Fraud Prevention
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              We use your personal information to detect, investigate or take
              action regarding possible fraudulent, illegal or malicious
              activity. If you choose to use the Services and register an
              account, you are responsible for keeping your account credentials
              safe. We highly recommend that you do not share your username,
              password, or other access details with anyone else. If you believe
              your account has been compromised, please contact us immediately.
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mt: 2, mb: 1 }}
            >
              Communicating with You and Service Improvement
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              We use your personal information to provide you with customer
              support and improve our Services. This is in our legitimate
              interests in order to be responsive to you, to provide effective
              services to you, and to maintain our business relationship with
              you.
            </Typography>
          </Box>

          {/* Cookies */}
          <Box component="section">
            <Typography
              variant="h5"
              sx={{
                fontFamily: headingFont,
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              Cookies
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              Like many websites, we use Cookies on our Site. For specific
              information about the Cookies that we use related to powering our
              store with Shopify, see https://www.shopify.com/legal/cookies. We
              use Cookies to power and improve our Site and our Services
              (including to remember your actions and preferences), to run
              analytics and better understand user interaction with the Services
              (in our legitimate interests to administer, improve and optimize
              the Services). We may also permit third parties and services
              providers to use Cookies on our Site to better tailor the
              services, products and advertising on our Site and other websites.
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              Most browsers automatically accept Cookies by default, but you can
              choose to set your browser to remove or reject Cookies through
              your browser controls. Please keep in mind that removing or
              blocking Cookies can negatively impact your user experience and
              may cause some of the Services, including certain features and
              general functionality, to work incorrectly or no longer be
              available. Additionally, blocking Cookies may not completely
              prevent how we share information with third parties such as our
              advertising partners.
            </Typography>
          </Box>

          {/* How We Disclose Personal Information */}
          <Box component="section">
            <Typography
              variant="h5"
              sx={{
                fontFamily: headingFont,
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              How We Disclose Personal Information
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              In certain circumstances, we may disclose your personal
              information to third parties for contract fulfillment purposes,
              legitimate purposes and other reasons subject to this Privacy
              Policy. Such circumstances may include:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  With vendors or other third parties who perform services on
                  our behalf (e.g., IT management, payment processing, data
                  analytics, customer support, cloud storage, fulfillment and
                  shipping).
                </Typography>
              </li>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  With business and marketing partners to provide services and
                  advertise to you. Our business and marketing partners will use
                  your information in accordance with their own privacy notices.
                </Typography>
              </li>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  When you direct, request us or otherwise consent to our
                  disclosure of certain information to third parties, such as to
                  ship you products or through your use of social media widgets
                  or login integrations, with your consent.
                </Typography>
              </li>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  With our affiliates or otherwise within our corporate group,
                  in our legitimate interests to run a successful business.
                </Typography>
              </li>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  In connection with a business transaction such as a merger or
                  bankruptcy, to comply with any applicable legal obligations
                  (including to respond to subpoenas, search warrants and
                  similar requests), to enforce any applicable terms of service,
                  and to protect or defend the Services, our rights, and the
                  rights of our users or others.
                </Typography>
              </li>
            </Box>

            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              We disclose the following categories of personal information and
              sensitive personal information about users for the purposes set
              out above in &quot;How we Collect and Use your Personal
              Information&quot; and &quot;How we Disclose Personal
              Information&quot;:
            </Typography>

            <Box sx={{ pl: 1, mb: 2 }}>
              <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                <strong>Categories:</strong> Identifiers such as basic contact
                details and certain order and account information; commercial
                information such as order information, shopping information and
                customer support information; internet or other similar network
                activity, such as Usage Data; geolocation data such as locations
                determined by an IP address or other technical measures.
              </Typography>
              <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8, mt: 1 }}>
                <strong>Categories of recipients:</strong> Vendors and third
                parties who perform services on our behalf (such as Internet
                service providers, payment processors, fulfillment partners,
                customer support partners and data analytics providers),
                business and marketing partners, and affiliates.
              </Typography>
            </Box>

            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              We do not use or disclose sensitive personal information without
              your consent or for the purposes of inferring characteristics
              about you.
            </Typography>
          </Box>

          {/* Third Party Websites */}
          <Box component="section">
            <Typography
              variant="h5"
              sx={{
                fontFamily: headingFont,
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              Third Party Websites and Links
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              Our Site may provide links to websites or other online platforms
              operated by third parties. If you follow links to sites not
              affiliated or controlled by us, you should review their privacy
              and security policies and other terms and conditions. We do not
              guarantee and are not responsible for the privacy or security of
              such sites, including the accuracy, completeness, or reliability
              of information found on these sites. Information you provide on
              public or semi-public venues, including information you share on
              third-party social networking platforms may also be viewable by
              other users of the Services and/or users of those third-party
              platforms without limitation as to its use by us or by a third
              party. Our inclusion of such links does not, by itself, imply any
              endorsement of the content on such platforms or of their owners or
              operators, except as disclosed on the Services.
            </Typography>
          </Box>

          {/* Children's Data */}
          <Box component="section">
            <Typography
              variant="h5"
              sx={{
                fontFamily: headingFont,
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              Children&apos;s Data
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              The Services are not intended to be used by children, and we do
              not knowingly collect any personal information about children. If
              you are the parent or guardian of a child who has provided us with
              their personal information, you may contact us using the contact
              details set out below to request that it be deleted.
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              As of the Effective Date of this Privacy Policy, we do not have
              actual knowledge that we &quot;share&quot; or &quot;sell&quot; (as
              those terms are defined in applicable law) personal information of
              individuals under 16 years of age.
            </Typography>
          </Box>

          {/* Security & Retention */}
          <Box component="section">
            <Typography
              variant="h5"
              sx={{
                fontFamily: headingFont,
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              Security and Retention of Your Information
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              Please be aware that no security measures are perfect or
              impenetrable, and we cannot guarantee &quot;perfect
              security.&quot; In addition, any information you send to us may
              not be secure while in transit. We recommend that you do not use
              insecure channels to communicate sensitive or confidential
              information to us.
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              How long we retain your personal information depends on different
              factors, such as whether we need the information to maintain your
              account, to provide the Services, comply with legal obligations,
              resolve disputes or enforce other applicable contracts and
              policies.
            </Typography>
          </Box>

          {/* Your Rights */}
          <Box component="section">
            <Typography
              variant="h5"
              sx={{
                fontFamily: headingFont,
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              Your Rights
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              Depending on where you live, you may have some or all of the
              rights listed below in relation to your personal information.
              However, these rights are not absolute, may apply only in certain
              circumstances and, in certain cases, we may decline your request
              as permitted by law.
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  <strong>Right to Access / Know:</strong> You may have a right
                  to request access to personal information that we hold about
                  you, including details relating to the ways in which we use
                  and share your information.
                </Typography>
              </li>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  <strong>Right to Delete:</strong> You may have a right to
                  request that we delete personal information we maintain about
                  you.
                </Typography>
              </li>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  <strong>Right to Correct:</strong> You may have a right to
                  request that we correct inaccurate personal information we
                  maintain about you.
                </Typography>
              </li>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  <strong>Right of Portability:</strong> You may have a right to
                  receive a copy of the personal information we hold about you
                  and to request that we transfer it to a third party, in
                  certain circumstances and with certain exceptions.
                </Typography>
              </li>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  <strong>Restriction of Processing:</strong> You may have the
                  right to ask us to stop or restrict our processing of personal
                  information.
                </Typography>
              </li>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  <strong>Withdrawal of Consent:</strong> Where we rely on
                  consent to process your personal information, you may have the
                  right to withdraw this consent.
                </Typography>
              </li>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  <strong>Appeal:</strong> You may have a right to appeal our
                  decision if we decline to process your request. You can do so
                  by replying directly to our denial.
                </Typography>
              </li>
              <li>
                <Typography sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
                  <strong>Managing Communication Preferences:</strong> We may
                  send you promotional emails, and you may opt out of receiving
                  these at any time by using the unsubscribe option displayed in
                  our emails to you. If you opt out, we may still send you
                  non-promotional emails, such as those about your account or
                  orders that you have made.
                </Typography>
              </li>
            </Box>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              You may exercise any of these rights where indicated on our Site
              or by contacting us using the contact details provided below.
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              We will not discriminate against you for exercising any of these
              rights. We may need to collect information from you to verify your
              identity, such as your email address or account information,
              before providing a substantive response to the request. In
              accordance with applicable laws, you may designate an authorized
              agent to make requests on your behalf to exercise your rights.
              Before accepting such a request from an agent, we will require
              that the agent provide proof you have authorized them to act on
              your behalf, and we may need you to verify your identity directly
              with us. We will respond to your request in a timely manner as
              required under applicable law.
            </Typography>
          </Box>

          {/* Complaints */}
          <Box component="section">
            <Typography
              variant="h5"
              sx={{
                fontFamily: headingFont,
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              Complaints
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              If you have complaints about how we process your personal
              information, please contact us using the contact details provided
              below. If you are not satisfied with our response to your
              complaint, depending on where you live you may have the right to
              appeal our decision by contacting us using the contact details set
              out below, or lodge your complaint with your local data protection
              authority.
            </Typography>
          </Box>

          {/* International Users */}
          <Box component="section">
            <Typography
              variant="h5"
              sx={{
                fontFamily: headingFont,
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              International Users
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              Please note that we may transfer, store and process your personal
              information outside the country you live in. Your personal
              information is also processed by staff and third party service
              providers and partners in these countries.
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              If we transfer your personal information out of Europe, we will
              rely on recognized transfer mechanisms like the European
              Commission&apos;s Standard Contractual Clauses, or any equivalent
              contracts issued by the relevant competent authority of the UK, as
              relevant, unless the data transfer is to a country that has been
              determined to provide an adequate level of protection.
            </Typography>
          </Box>

          {/* Contact */}
          <Box component="section">
            <Typography
              variant="h5"
              sx={{
                fontFamily: headingFont,
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              Contact
            </Typography>
            <Typography paragraph sx={{ fontSize: ".98rem", lineHeight: 1.8 }}>
              Should you have any questions about our privacy practices or this
              Privacy Policy, or if you would like to exercise any of the rights
              available to you, please call or email us at{" "}
              kaleidoscope.pk@gmail.com or contact us at Al Noor Garden, 38000,
              PK.
            </Typography>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;
